import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { Arrow, General, HelpCircleFilled, Users } from 'icons/untitledui';
import colours from 'theme/atoms/colours';
import * as Buttons from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';
import * as Styled from 'shiftNotes/styles/NameDropdown.style';
import { Profile } from 'teamProfiles/_types';
import { useTranslation } from 'react-i18next';
import routesConfig from 'constants/routesConfig.json';
import useClickOutside from 'shared/hooks/useClickOutside';
import useControllableOpen from 'shared/hooks/useControllableOpen';
import useListboxKeyboardNavigation from 'shared/hooks/useListboxKeyboardNavigation';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';

interface NameDropdownProps {
  name: string;
  profiles: Profile[];
  initialProfileId?: string;
  hasProfile?: boolean;
  disabled?: boolean;
  entityStartPosition?: number;
  entityExtractionId?: string;
  noteId?: string;
  onProfileChange?: (startPosition: number, newProfileId: string) => void;
  onSelectProfile?: (profile: Profile) => void;
  autoOpen?: boolean;
  onClose?: () => void;
  hiddenTrigger?: boolean;
  anchorPosition?: { top: number; left: number };
  anchorRef?: React.RefObject<HTMLElement | null>;
}

export default function NameDropdown({
  name,
  profiles,
  initialProfileId,
  hasProfile = true,
  disabled = false,
  entityStartPosition,
  entityExtractionId,
  noteId,
  onProfileChange,
  onSelectProfile,
  autoOpen = false,
  onClose,
  hiddenTrigger = false,
  anchorPosition,
  anchorRef,
}: NameDropdownProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const history = useHistory();
  const shiftNotesCapture = usePosthogCapture();
  const [isOpen, setIsOpen] = useControllableOpen();
  const [selectedProfileId, setSelectedProfileId] = useState(initialProfileId);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState(
    anchorPosition || { top: 0, left: 0 }
  );
  const [searchReadOnly, setSearchReadOnly] = useState(true);
  const containerRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);
  const selectedProfile = profiles.find((p) => p.id === selectedProfileId);
  const filteredProfiles = profiles
    .filter((p) =>
      p.full_name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => a.full_name.localeCompare(b.full_name));

  useClickOutside([containerRef, menuRef], () => setIsOpen(false), isOpen);

  const { onTriggerKeyDown, createOptionKeyDownHandler } =
    useListboxKeyboardNavigation({
      open: isOpen,
      setOpen: setIsOpen,
      triggerRef: triggerRef as React.RefObject<HTMLButtonElement | null>,
      listRef,
      selectedOptionSelector: `[data-selection-option="true"][data-selected="true"]`,
    });

  useEffect(() => {
    const updatePosition = () => {
      if (!isOpen) return;

      let measureTarget: HTMLElement | null = null;
      if (hiddenTrigger) {
        measureTarget = anchorRef?.current ?? null;
      } else {
        measureTarget = triggerRef.current;
      }

      if (measureTarget) {
        const rect = measureTarget.getBoundingClientRect();
        const menuWidth = 298;
        const margin = 8;
        const left = Math.max(
          margin + window.scrollX,
          Math.min(
            rect.left + window.scrollX,
            window.innerWidth - menuWidth - margin + window.scrollX
          )
        );
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left,
        });
        return;
      }

      if (hiddenTrigger && anchorPosition) {
        setDropdownPosition(anchorPosition);
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, hiddenTrigger, anchorPosition, anchorRef]);

  useEffect(() => {
    if (isOpen && selectedOptionRef.current) {
      selectedOptionRef.current.scrollIntoView({ block: 'center' });
    }
    if (isOpen) {
      setSearchReadOnly(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (autoOpen && !isOpen && !disabled) {
      setIsOpen(true);
    } else if (!autoOpen && isOpen && disabled) {
      setIsOpen(false);
    }
  }, [autoOpen, disabled, isOpen, setIsOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen && onClose) {
      onClose();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, onClose]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    if (!isOpen) {
      shiftNotesCapture.noteProfileDropdownOpened({ has_profile: hasProfile });
    }
    setIsOpen(!isOpen);
  };

  const handleSelectProfile = useCallback(
    (profile: Profile) => {
      const isChanging = profile.id !== initialProfileId;
      setSelectedProfileId(profile.id);
      setIsOpen(false);
      triggerRef.current?.focus();

      if (isChanging && onProfileChange && entityStartPosition !== undefined) {
        onProfileChange(entityStartPosition, profile.id);
      }

      if (onSelectProfile) {
        onSelectProfile(profile);
      }
    },
    [
      initialProfileId,
      setIsOpen,
      onProfileChange,
      entityStartPosition,
      onSelectProfile,
    ]
  );

  const handleCreateNew = () => {
    shiftNotesCapture.noteProfileCreateNewClicked({
      has_query: searchQuery.trim().length > 0,
    });
    const params = new URLSearchParams();
    const nameToUse = searchQuery || name;
    if (nameToUse) {
      params.set('full_name', nameToUse);
    }
    if (entityExtractionId) {
      params.set('entity_extraction_id', entityExtractionId);
    }
    if (noteId) {
      params.set('note_id', noteId);
    }
    const queryString = params.toString();
    history.push(
      `${routesConfig.TEAM_PAGE}/new${queryString ? `?${queryString}` : ''}`
    );
  };

  const getInitials = (fullName: string): string => {
    const cleaned = fullName.replace(/\s*\(.*?\)/g, '').trim();
    const parts = cleaned.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return cleaned.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <Styled.Container
        ref={containerRef}
        aria-hidden={hiddenTrigger || undefined}
        style={
          hiddenTrigger
            ? {
                position: 'absolute',
                width: 0,
                height: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }
            : undefined
        }
      >
        <Styled.Trigger
          ref={triggerRef}
          $isOpen={isOpen}
          onClick={handleToggle}
          onKeyDown={
            disabled
              ? undefined
              : (onTriggerKeyDown as unknown as React.KeyboardEventHandler<HTMLSpanElement>)
          }
          style={
            hiddenTrigger
              ? {
                  display: 'block',
                  opacity: 0,
                  width: 0,
                  height: 0,
                  padding: 0,
                  margin: 0,
                  overflow: 'hidden',
                  pointerEvents: 'none',
                }
              : { cursor: disabled ? 'default' : 'pointer' }
          }
        >
          {hasProfile || selectedProfileId ? (
            <Styled.ProfileIconUser>
              <Users.User02 size={15} />
            </Styled.ProfileIconUser>
          ) : (
            <Styled.ProfileIcon>
              <HelpCircleFilled size={15} color={colours.blue[500]} />
            </Styled.ProfileIcon>
          )}
          {selectedProfile?.full_name || name}
          {!disabled && (
            <Styled.ChevronIcon $isOpen={isOpen}>
              <Arrow.ChevronDown size={15} />
            </Styled.ChevronIcon>
          )}
        </Styled.Trigger>
      </Styled.Container>
      {isOpen &&
        createPortal(
          <Styled.DropdownMenu
            ref={menuRef}
            data-name-dropdown-menu
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <Styled.SearchContainer>
              <Styled.SearchInput
                ref={searchInputRef}
                type="text"
                readOnly={searchReadOnly}
                placeholder={t('nameDropdown.chooseAnotherProfile')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (searchReadOnly) {
                    setSearchReadOnly(false);
                    searchInputRef.current?.focus();
                  }
                }}
              />
              <Styled.SearchIcon>
                <General.SearchSm size={20} />
              </Styled.SearchIcon>
            </Styled.SearchContainer>
            <Styled.OptionsList ref={listRef}>
              {filteredProfiles.map((profile) => {
                const isSelected = profile.id === selectedProfileId;
                return (
                  <Styled.Option
                    key={profile.id}
                    ref={isSelected ? selectedOptionRef : null}
                    type="button"
                    $isSelected={isSelected}
                    data-selection-option="true"
                    data-selected={isSelected ? 'true' : 'false'}
                    onClick={() => handleSelectProfile(profile)}
                    onKeyDown={createOptionKeyDownHandler(() =>
                      handleSelectProfile(profile)
                    )}
                  >
                    <Styled.Left $isSelected={isSelected}>
                      <Styled.Avatar $isSelected={isSelected}>
                        {getInitials(profile.full_name)}
                      </Styled.Avatar>
                      <Styled.NameInfo>
                        <Text.P2Semibold>{profile.full_name}</Text.P2Semibold>
                        {profile.role && (
                          <Styled.ProfileId>{profile.role}</Styled.ProfileId>
                        )}
                      </Styled.NameInfo>
                    </Styled.Left>
                    <Styled.SelectedIndicator $selected={isSelected}>
                      {isSelected && <General.Check size={16} />}
                    </Styled.SelectedIndicator>
                  </Styled.Option>
                );
              })}
            </Styled.OptionsList>
            <Buttons.SecondaryLight
              $size="medium"
              stretch
              onClick={handleCreateNew}
              style={{ justifyContent: 'space-between' }}
            >
              <Text.P2Semibold>
                {!hasProfile && !selectedProfileId
                  ? t('nameDropdown.newMemberWithName', {
                      name:
                        (searchQuery || name).length > 20
                          ? `${(searchQuery || name).slice(0, 20)}...`
                          : searchQuery || name,
                    })
                  : t('nameDropdown.newMember')}
              </Text.P2Semibold>
              <General.PlusCircle />
            </Buttons.SecondaryLight>
          </Styled.DropdownMenu>,
          document.body
        )}
    </>
  );
}
